import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  DataFetcher,
  Store,
  middlewareTypes,
  createInstrumentationMiddleware,
  createErrorMiddleware,
} from '@bingads-webui-react/overreact';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { useCCEnvironment } from '@bingads-webui/overreact-cc';
import {
  getRequestor as getCampaignRequestor,
  getEnvironmentInstance as getCampaignEnvInstance,
  middlewares,
} from '@bingads-webui/overreact-odata-campaign';
import { getDefaultHeaderObj } from '@bingads-webui/overreact-bingads';
import { getConfig } from '@bingads-webui-campaign/odata-config';
import { redirectToLogin } from '@bingads-webui-campaign/global-app-init';

const ExpectedErrorStrings = [
  'TypeError: Failed to fetch',
  'TypeError: Load failed',
  'TypeError: NetworkError when attempting to fetch resource',
  '{"readyState":0,"status":0,"statusText":"error","headers":{}}',
].map(errorString => errorString.toLowerCase());
function isExpectedErrorString(error) {
  if (!error) {
    return false;
  }

  const errorMessage = _.isError(error) ? error : JSON.stringify(error);
  const errorMessageInLowerCase = _.isFunction(_.get(errorMessage, 'toLowerCase')) ? errorMessage.toLowerCase() : '';
  return _.some(
    ExpectedErrorStrings,
    errorString => errorMessageInLowerCase.indexOf(errorString) > -1
  );
}

const store = new Store();

function useCampaignEnvironment(params) {
  const {
    serviceRoot, token, middleWares,
  } = params;

  return useMemo(() => {
    const requestor = getCampaignRequestor(
      serviceRoot,
      {
        ...getDefaultHeaderObj(),
        Authorization: `CCMTSmallToken ${token}`,
      }
    );

    return getCampaignEnvInstance(requestor, store, middleWares);
  }, [middleWares, serviceRoot, token]);
}

export const OverreactDataFetcher = (props) => {
  const serverContext = useContext(ServerContext);
  const {
    Token,
    CurrentLocale,
    ServicesEndpoints: {
      ClientCenterUIAPI,
      CampaignAPI, // path to ODataApi root, should have trailing slash
    },
    PageTrackingId,
    CurrentUser,
  } = serverContext;
  const CampaignAPIPath = 'Campaign/V2';
  const CultureLcid = serverContext.CurrentUser.LocaleID;

  const {
    LocaleID,
    Locale,
  } = CurrentUser || {};

  const lcid = CultureLcid || LocaleID || Locale;
  const instrumentation = useContext(InstrumentationContext);
  const activity = useMemo(
    () => {
      const scenario = instrumentation && instrumentation.overreactRequest && instrumentation.overreactRequest.create();
      const act = scenario ? scenario.logger.create() : {
        trace: _.noop,
        error: _.noop,
        ajax: _.noop,
      };
      return act;
    },
    [instrumentation]
  );

  const errorProcessor = useCallback((error) => {
    if (error && error.status === 401) {
      activity.trace('overreact data fetcher starts sign out redirection');
      redirectToLogin(serverContext);
    }
  }, [activity, serverContext]);

  const instrumentationMiddleware = useMemo(() => ({
    [middlewareTypes.ERROR]: createErrorMiddleware({
      errorProcessor,
    }),
    [middlewareTypes.INSTRUMENTATION]: createInstrumentationMiddleware({
      pageTrackingId: PageTrackingId,
      // traceFunc is called on error and the error is mapped to user error using errorMappers
      // errorFunc is called on error (no result - server or network error)
      // perfFunc is called pre and post request
      loggerFunc: {
        traceFunc: ({
          requestId, message, api, httpMethod,
        }) => {
          activity.trace(message || '', api, requestId, httpMethod);
        },
        errorFunc: ({
          requestId, api, httpMethod, message,
        }) => {
          activity.error(message || '', api, requestId, null /* impact user */, httpMethod);
        },
        perfFunc: ({
          requestId, api, httpMethod, timeTaken, message, isMethodEnter,
        }) => {
          activity.ajax(
            requestId, api, isMethodEnter,
            httpMethod, timeTaken, null /* pass */, message
          );
        },
      },
      errorMappers: [{
        check: (error) => {
          if (!error) {
            return false;
          }
          return isExpectedErrorString(error);
        },
      }],
      stubOptions: { lcid },
    }),
  }), [PageTrackingId, activity, errorProcessor, lcid]);

  const allMiddlewares = useMemo(
    () => ({ ...middlewares, ...instrumentationMiddleware }),
    [instrumentationMiddleware]
  );

  const campaignOdataRoot = useMemo(() => {
    const odataConfig = getConfig();
    const odataPath = CampaignAPIPath || odataConfig.odataPath;
    return `${CampaignAPI}${odataPath}`;
  }, [CampaignAPI, CampaignAPIPath]);

  const overreactCcEnv = useCCEnvironment({
    serviceRoot: ClientCenterUIAPI,
    store,
    middlewares: allMiddlewares,
    locale: CurrentLocale,
  });

  const overreactCampaignEnv = useCampaignEnvironment({
    serviceRoot: campaignOdataRoot,
    token: Token,
    middlewares: allMiddlewares,
  });

  return (
    <DataFetcher
      environment={overreactCcEnv}
      environments={[
        overreactCcEnv,
        overreactCampaignEnv,
      ]}
    >
      {props.children}
    </DataFetcher>
  );
};

OverreactDataFetcher.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
